<script setup>
import {
  NConfigProvider,
  NDialogProvider,
  NGlobalStyle,
  NLoadingBarProvider,
  NMessageProvider,
  NThemeEditor,
  deDE,
  enUS,
  frFR,
  nlNL,
} from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { useHead } from '@vueuse/head'
import { computed } from 'vue'
import GlobalUtilProvider from './components/utils/GlobalUtilProvider.vue'
import customTheme from './customTheme.json'
import { useAppSettingsStore } from './stores/appSettings'
import { useAuthStore } from './stores/auth'
import naiveI18nDate from './naivelocales/naive-i18n-dates'
import { usePageTitle } from './composables/pageTitle'
import { setLanguage as setHttpHeaderLanguage } from '@/http'
import { useOrdersStore } from './stores/orders'

const orderStore = useOrdersStore
const i18n = useI18n()
// const t = i18n.t

const appSettings = useAppSettingsStore()

let showHelpModal = ref(false);

useHead({
  htmlAttrs: {
    lang: computed(() => i18n.locale.value),
  },
})

const authStore = useAuthStore()

function setupLanguages() {
  if (authStore.user?.language) {
    i18n.locale.value = authStore.user.language
    setHttpHeaderLanguage(authStore.user.language)
    return
  }

  if (appSettings.appLanguage) {
    if (i18n.availableLocales.includes(appSettings.appLanguage)) {
      i18n.locale.value = appSettings.appLanguage
      setHttpHeaderLanguage(appSettings.appLanguage)
      return
    }
  }
  for (const lang of navigator.languages) {
    for (const appLang of i18n.availableLocales) {
      if (lang.includes(appLang)) {
        i18n.locale.value = appLang
        setHttpHeaderLanguage(appLang)
        return
      }
    }
  }
  i18n.locale.value = 'nl'
  setHttpHeaderLanguage('nl')
}

setupLanguages()

watch(() => i18n.locale.value, (newLocale) => {
  // console.log('WATCH: Setting language to', newLocale)
  appSettings.appLanguage = newLocale
  setHttpHeaderLanguage(newLocale)
  if (authStore.user && authStore.user.language !== newLocale)
  // do the call to the backend to update the user language
    authStore.setCurrentUserLanguage(newLocale)
})

watch(() => authStore.user?.language, (userLocale, oldUserLocale) => {
  if (userLocale === oldUserLocale)
    return
  i18n.locale.value = userLocale
  setHttpHeaderLanguage(userLocale)
  
})


const mailToLink = computed(() => {
  const orderStore = useOrdersStore()
  const recipient = 'cathy.osan@robojob.eu'
  const serial = orderStore.order?.serial_number ? `[${orderStore.order.serial_number}]` : ''
  const orderNr =  orderStore.order?.title ?? ''
  const subject = `Customer Portal help request ${serial} ${orderNr}`
  return `mailto:${recipient}?subject=${encodeURIComponent(subject)}`
})

usePageTitle(null)


// NaiveUI internationalization
const naiveI18n = computed(() => {
  switch (i18n.locale.value) {
    case 'nl':
      return nlNL
    case 'fr':
      return frFR
    case 'de':
      return deDE
    case 'en':
      return enUS
    default:
      return enUS
  }
})

const isDev = import.meta.env.DEV

function handleClick() {
  console.log('lets get some help');
  showHelpModal.value = true;
}

</script>

<template>
  <NConfigProvider
    :locale="naiveI18n"
    :date-locale="naiveI18nDate"
    :theme-overrides="customTheme"
    inline-theme-disabled
  >
    <component :is="false ? NThemeEditor : 'section'">
      <NLoadingBarProvider>
        <NMessageProvider>
          <NDialogProvider>
            <ResponsiveWarning>
              <router-view />
            </ResponsiveWarning>
          </NDialogProvider>
          <GlobalUtilProvider />
        </NMessageProvider>
      </NLoadingBarProvider>
      <NGlobalStyle />
    </component>
  </NConfigProvider>
  
  <n-modal v-model:show="showHelpModal">
      <div class="relative rounded-lg">
        <div class="rounded-lg overflow-hidden relative bg-white px-20 py-10 max-w-200">
          <div style="text-align:center; font-size: 36px;">Contact RoboJob</div>
          <div v-html="$t('general.helpModalText')" class="multiline-text"></div>
          <div style="display: grid; grid-template-columns: auto auto; gap: 10px; margin: 30px; place-content: center;">
            <div style="font-size: 20px; display: flex; justify-content: center; align-items: center;" class="first-column">
              <n-icon><MdiEmailOutline/></n-icon>
            </div>
            <div style="text-decoration: underline;"><a :href="mailToLink">customerportal@robojob.eu</a></div>
            <div style="font-size: 20px; display: flex; justify-content: center; align-items: center;" class="first-column">
              <n-icon><MdiPhoneDial/></n-icon>
            </div>
            <div>+32 (0)15 25 72 74</div>
            <div style="font-size: 20px; display: flex; justify-content: center; align-items: center;" class="first-column">
              <n-icon><MdiGlobe/></n-icon>
            </div>
            <div style="text-decoration: underline;"><a href="https://www.robojob.eu" target="_blank">https://www.robojob.eu</a></div>
          </div>
        </div>
      </div>
  </n-modal>


  <a :href="mailToLink" role="button" @click.prevent="handleClick" class="help-button" target="_blank" aria-label="Need Support?" v-if="authStore.user?.permissions.see_needhelp_button ?? false">
    <span class="help-text">{{ $t('general.supportButton') }}</span>
    <span class="help-icon">
        <n-icon>
          <MdiHelpCircleOutline />
        </n-icon>
    </span>
  </a>
</template>


<style lang="scss">

.multiline-text {
  white-space: pre-line;
}

body {
  @apply font-sans;
}

    /* Align all items in the first column to the right */
    .first-column {
      text-align: right;
    }

.help-button {
  position: fixed;
  bottom: 100px;
  right: 0;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: #065FFCFF;
  color: white;
  text-decoration: none;
  font-family: sans-serif;
  font-size: 14px;

  height: 48px;
  padding: 0 0px 0px 10px;
  border-top-left-radius: 24px;
  border-bottom-left-radius: 24px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;

  box-shadow: -2px 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  z-index: 1000;
  transition: background-color 0.2s ease;
}

/* Help text is initially hidden and takes no space */
.help-text {
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  white-space: nowrap;
  margin-right: 0;
  transition: max-width 0.5s ease, opacity 0.5s ease, margin-right 0.5s ease;
}

/* On hover: expand text area naturally */
.help-button:hover {
  background-color: rgb(46, 121, 252);
}

.help-button:hover .help-text {
  max-width: 300px; /* large enough to fit any language */
  opacity: 1;
}

/* The icon is always visible and aligned right */
.help-icon {
  display: flex;
  align-items: center; /* vertically centers children */
  justify-content: flex-end;

  font-size: 32px;  
  flex-shrink: 0;
  padding: 10px;
}

</style>
